import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import RadioButton from 'design-system/RadioButton';
import { formatPlural } from 'utils';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import IconTransactionCategory from 'features/transactions/icons/IconTransactionCategory';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { useAppSelector } from 'store/hooks';
import EmojiText from 'design-system/EmojiText';
import { selectNormalizedCategoriesWithTransactionsCount } from 'reducers/selectors';
import { isWeb } from '../../../../constants';
const KeyboardCategoriesRow = ({ item, onPress, activeFilterMap, type, isOtherCategorySelected }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const categories = useAppSelector(selectNormalizedCategoriesWithTransactionsCount);
    const isParentCategorySelected = !!activeFilterMap?.[item.id];
    const isSubcategorySelected = !!item.subCategoryIds?.some((id) => activeFilterMap?.[id]);
    const subcategories = useMemo(() => item.subCategoryIds
        ?.map((id) => categories[id])
        .filter((subcategory) => !!subcategory), [item.subCategoryIds, categories]);
    const otherTransactionsCount = useMemo(() => (item.transactionsCount ?? 0) -
        (subcategories?.reduce((acc, subcategory) => acc + (subcategory.transactionsCount ?? 0), 0) ?? 0), [item.transactionsCount, subcategories]);
    const subcategoriesHeight = useSharedValue(undefined);
    const showSubcategories = isParentCategorySelected || isSubcategorySelected || isOtherCategorySelected;
    const showOnInitialLoad = showSubcategories && !subcategoriesHeight.value;
    const subcategoriesAnimatedStyle = useAnimatedStyle(() => {
        if (showOnInitialLoad)
            return {};
        return {
            opacity: withTiming(showSubcategories ? 1 : 0),
            height: withTiming(showSubcategories ? subcategoriesHeight.value ?? 0 : 0),
        };
    });
    const onLayout = useCallback((event) => {
        subcategoriesHeight.value = event.nativeEvent.layout.height;
    }, [subcategoriesHeight]);
    return (<TouchableHighlight onCard hoverOnLight onPress={() => onPress(item)} activeOpacity={1} underlayColor={colors.background.underlay}>
      <View>
        <View style={styles.container}>
          <RadioButton isChecked={isParentCategorySelected || isSubcategorySelected} type={type}/>
          <Spacer w={16}/>
          <View style={styles.icon}>
            <IconTransactionCategory category={item}/>
          </View>
          <Spacer w={16}/>
          <View style={styles.main}>
            <Text Text-16 numberOfLines={1}>
              {item.displayName}
            </Text>
            {typeof item.transactionsCount === 'number' && (<Text TextThin-14 Secondary>
                {item.transactionsCount} {formatPlural('transaction', 'transactions', item.transactionsCount)}
              </Text>)}
          </View>
        </View>
        {!!subcategories?.length && (<Animated.View style={subcategoriesAnimatedStyle}>
            <View onLayout={onLayout} style={[styles.subcategories, showOnInitialLoad ? { position: 'relative' } : undefined]}>
              {subcategories.map((subcategory) => {
                const isSelected = activeFilterMap?.[subcategory.id];
                return (<TouchableBounce key={subcategory.id} style={[styles.row, isSelected && styles.selectedButton]} onPress={() => onPress(subcategory)}>
                    <EmojiText>{subcategory.emoji || ''}</EmojiText>
                    <Spacer.W8 />
                    <Text Text-14 {...(isSelected ? { Primary: true } : {})}>
                      {subcategory.displayName}{' '}
                      <Text TextThin-14 {...(isSelected ? { Primary: true } : { Secondary: true })}>
                        ({subcategory.transactionsCount})
                      </Text>
                    </Text>
                  </TouchableBounce>);
            })}
              {!!otherTransactionsCount && (<TouchableBounce style={[styles.row, isOtherCategorySelected && styles.selectedButton]} onPress={() => onPress({ ...item, displayName: 'Others', parentCategoryId: item.id })}>
                  <Spacer w={2}/>
                  <Text Text-14 {...(isOtherCategorySelected ? { Primary: true } : {})}>
                    Others{' '}
                    <Text TextThin-14 {...(isOtherCategorySelected ? { Primary: true } : { Secondary: true })}>
                      ({otherTransactionsCount})
                    </Text>
                  </Text>
                </TouchableBounce>)}
            </View>
          </Animated.View>)}
      </View>
    </TouchableHighlight>);
};
const ICON_SIZE = 44;
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingVertical: rem(16),
        paddingHorizontal: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
    icon: {
        width: rem(ICON_SIZE),
        height: rem(ICON_SIZE),
        borderRadius: rem(ICON_SIZE),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.dark,
    },
    main: {
        flex: 1,
        justifyContent: 'center',
    },
    subcategories: {
        flexWrap: 'wrap',
        flexDirection: 'row',
        gap: rem(8),
        position: 'absolute',
        marginLeft: rem(56),
        marginRight: rem(16),
        paddingBottom: rem(16),
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingRight: rem(12),
        paddingLeft: rem(10),
        paddingVertical: rem(9),
        backgroundColor: colors.background.dark,
        borderRadius: rem(12),
        borderWidth: 1,
        borderColor: 'transparent',
    },
    selectedButton: {
        borderWidth: 1,
        borderColor: colors.elements.brand,
    },
}));
export default KeyboardCategoriesRow;
